// ── companies-collection: useCollection client adapter for /companies ────────

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  humanizeLabel,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import { destructiveButtonClass } from "../../components/ui-classes";
import type { CoachingCompanyDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import { COMPANY_STATUSES } from "./companies-write";

export const COMPANIES_PAGE_SIZE = 25;
export const COMPANIES_DEFAULT_SORT = { id: "name", dir: "asc" as const };

export type CompanyRow = CoachingCompanyDto & Record<string, unknown>;

export interface CompaniesCollectionActions {
  allowUpdate: boolean;
  onEdit: (row: CoachingCompanyDto) => void;
  onArchive: (row: CoachingCompanyDto) => void;
}

function statusVariant(status: string | undefined): "success" | "warning" {
  return status === "active" || status == null ? "success" : "warning";
}

export function buildCompaniesFilterDefs(): CollectionFilterDef<CompanyRow>[] {
  return [
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "active",
      options: COMPANY_STATUSES.map((status) => ({
        value: status,
        label: humanizeLabel(status)
      }))
    }
  ];
}

export function buildCompaniesCollectionColumns(
  actions: CompaniesCollectionActions
): CollectionColumnDef<CompanyRow>[] {
  const columns: CollectionColumnDef<CompanyRow>[] = [
    {
      id: "name",
      header: "Company",
      sortable: true,
      sortAccessor: (row) => row.name ?? "",
      searchValue: (row) => `${row.name ?? ""} ${row.id}`,
      priority: 1,
      cell: (row) => (
        <Link
          to={`/companies/${encodeURIComponent(row.id)}`}
          className="font-medium text-link no-underline hover:underline"
          data-exact-set-name="Open company">
          {row.name || "Unnamed company"}
        </Link>
      )
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status ?? "active",
      priority: 2,
      cell: (row) => (
        <Badge variant={statusVariant(row.status)}>{humanizeLabel(row.status ?? "active")}</Badge>
      )
    },
    {
      id: "id",
      header: "Reference",
      sortable: false,
      priority: 3,
      cell: (row) => <span className="tabular-nums text-ink-muted">{row.id}</span>
    }
  ];

  if (actions.allowUpdate) {
    columns.push({
      id: "_actions",
      header: "",
      sortable: false,
      priority: 1,
      cell: (row) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => actions.onEdit(row)} aria-label={`Edit ${row.name}`} data-exact-set-name="Edit company">
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            className={destructiveButtonClass}
            onClick={() => actions.onArchive(row)}
            aria-label={`Archive ${row.name}`}
            disabled={row.status === "archived"}
            data-exact-set-name="Archive company">
            Archive
          </Button>
        </div>
      )
    });
  }

  return columns;
}

export function useCompaniesCollection(
  rows: CoachingCompanyDto[],
  actions: CompaniesCollectionActions,
  routePath?: string
): UseCollectionResult<CompanyRow> {
  const filters = useMemo(() => buildCompaniesFilterDefs(), []);
  const columns = useMemo(() => buildCompaniesCollectionColumns(actions), [actions]);
  const initialState = useMemo(
    () => ({ sort: COMPANIES_DEFAULT_SORT, ...readDirectoryInitialState(routePath, COMPANIES_PAGE_SIZE) }),
    [routePath]
  );

  return useCollection<CompanyRow>({
    mode: "client",
    rows: rows as CompanyRow[],
    columns,
    filters,
    pageSize: COMPANIES_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-companies",
    initialState,
    getRowKey: (row) => row.id
  });
}
